import { IRequirementRowPadding } from 'app/models/Placement/PlacementSummary';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useRequirementFilter } from '../../../Helpers/requirementFilter';
import {
  selectEditableFields,
  selectEditableRequirementTypes,
  selectOfferInfo,
} from '../../../../../../../../../store/redux-store/placement-summary/selectors';
import { OfferConfirmationCheckbox } from './OfferConfirmationCheckbox';
import { ConfirmationsRow } from '../../TableComponents/Confirmations/ConfirmationsTable';
import { selectPlacementStatus } from 'store/redux-store/placement-status/selectors';
import { TransportationTypes } from '../Transportation/TransportationWrapper';
import { CustomTooltip } from '@AMIEWEB/Common';
import { selectPlacementOrderType } from 'store/redux-store/placement-details/selectors';
import { PlacementOrderType } from 'app/models/Placement/PlacementDetails';
import { convertToPST } from 'app/helpers/dateHelper';
import { selectUser } from 'oidc/user.selectors';
import { Concatenate } from 'utils/string/string';
import { IUserInfo } from 'oidc/user.redux';
import { useArrivedFieldsetMeta } from '../useArrivedFieldsetMeta';

export const DatesConfirmed = ({ hasRequirements }: IRequirementRowPadding) => {
  const { t } = useTranslation();
  const { control, getValues, setValue } = useFormContext();
  const { userInfo } = useSelector(selectUser);

  const { currentPlacementStatus } = useSelector(selectPlacementStatus);
  const { datesConfirmed: initialValue } = useSelector(selectEditableFields);
  const { requirementFilter } = useSelector(selectOfferInfo);
  const { datesConfirmed } = useSelector(selectEditableRequirementTypes);
  const placementOrderType = useSelector(selectPlacementOrderType);
  const arrivedFieldsetMeta = useArrivedFieldsetMeta();

  const isStrike = placementOrderType === PlacementOrderType.StrikeOrderInHsg;
  const { display } = useRequirementFilter('offer-confirmation', requirementFilter, datesConfirmed?.requirement);

  const [selectedTransport, arrivalDate, arrivalTime, departureAirport, dob, gender] = useWatch({
    control,
    name: ['transportationChosen', 'arrivalDate', 'arrivalTime', 'departureAirport', 'dob', 'gender'],
  });

  const [tooltipContent, setTooltipContent] = useState<string>('');

  // consolidated validation logic
  // determines if fields are missing based on transport type
  const validationState = useMemo(() => {
    const transportId = selectedTransport?.transportationId;
    const isGenderMissing = !gender?.object && !gender?.label;
    const isFlyDemographicsMissing = isStrike && (!dob || isGenderMissing);

    if (transportId === TransportationTypes.UNKNOWN) {
      return {
        isInvalid: true,
        message: t('placement.profile.summary.offer.confirmations.unknownTransportationMessage'),
      };
    }

    if (transportId === TransportationTypes.DRIVE) {
      const isMissing = !arrivalDate || !arrivalTime;
      return {
        isInvalid: isMissing,
        message: isMissing ? t('placement.profile.summary.offer.confirmations.driveTransportationMessage') : '',
      };
    }

    if (transportId === TransportationTypes.FLY) {
      const isMissing = !arrivalDate || !departureAirport || isFlyDemographicsMissing;
      return {
        isInvalid: isMissing,
        message: isMissing ? t('placement.profile.summary.offer.confirmations.flyTransportationMessage') : '',
      };
    }

    return { isInvalid: false, message: '' };
  }, [selectedTransport, arrivalDate, arrivalTime, departureAirport, dob, gender, isStrike, t]);

  // helper to auto-uncheck fields if data becomes invalid
  const uncheckInvalidFields = useCallback(() => {
    const travelConfirmed = getValues('travelConfirmed');
    const datesConfirmedValue = getValues('datesConfirmed');

    // proceed only if at least one field is currently checked
    if (!travelConfirmed?.checked && !datesConfirmedValue?.checked) return;

    const meta = getLastUpdatedMeta(userInfo);
    const options = { shouldDirty: true };

    if (travelConfirmed?.checked) {
      setValue('travelConfirmed', { checked: false, ...meta }, options);
    }
    if (datesConfirmedValue?.checked) {
      setValue('datesConfirmed', { checked: false, ...meta }, options);
    }
  }, [getValues, setValue, userInfo]);

  // handle tooltips & auto-unchecking
  useEffect(() => {
    if (!isStrike) {
      setTooltipContent('');
      return;
    }

    setTooltipContent(validationState.message);

    if (validationState.isInvalid) {
      uncheckInvalidFields();
    }
  }, [isStrike, validationState, uncheckInvalidFields]);

  const isCheckboxDisabled = useMemo(() => {
    if (arrivedFieldsetMeta.disabled) return true;

    // disable only if validation fails for strike orders
    if (isStrike) return validationState.isInvalid;

    // for regular orders, disable if validation fails or status is restricted
    const isStatusRestricted =
      currentPlacementStatus?.activityStatusId >= 15 && currentPlacementStatus?.activityStatusId !== 16;

    return validationState.isInvalid || isStatusRestricted;
  }, [arrivedFieldsetMeta.disabled, isStrike, validationState.isInvalid, currentPlacementStatus]);

  if (!display) return null;

  return (
    <Controller
      control={control}
      name="datesConfirmed"
      render={({ field: { onChange, ...rest } }) => (
        <ConfirmationsRow
          id="placement-offer-confirmations-dates-confirmed"
          lastUpdatedBy={rest?.value?.lastUpdatedBy}
          lastUpdatedDate={rest?.value?.lastUpdatedAt}
          requirement={datesConfirmed?.requirement}
          hasOtherRequirements={hasRequirements}
          controlComponent={
            <CustomTooltip
              disabled={isStrike ? !validationState.isInvalid : true}
              tooltipContent={tooltipContent}
            >
              <OfferConfirmationCheckbox
                label={t('placement.profile.summary.offer.confirmations.datesConfirmed')}
                defaultState={initialValue}
                disabled={isCheckboxDisabled}
                onChange={(data) => {
                  // if unchecking "dates confirmed", also uncheck "travel confirmed"
                  if (!data?.checked) {
                    const currentTravelConfirmed = getValues('travelConfirmed');
                    if (currentTravelConfirmed?.checked) {
                      const meta = getLastUpdatedMeta(userInfo);
                      setValue('travelConfirmed', { checked: false, ...meta }, { shouldDirty: true });
                    }
                  }
                  onChange(data);
                }}
                {...rest}
              />
            </CustomTooltip>
          }
        />
      )}
    />
  );
};

const getLastUpdatedMeta = (userInfo: Pick<IUserInfo, 'firstName' | 'lastName' | 'employeeId'>) => {
  const now = convertToPST(new Date(), 'yyyy-MM-DDTHH:mm:ss');
  const userName = Concatenate([userInfo?.firstName || '', userInfo?.lastName || ''], ' ');
  const userId = userInfo?.employeeId || null;
  return { lastUpdatedBy: userName, lastUpdatedById: userId, lastUpdatedAt: now };
};
